import React, { useState, useEffect } from 'react';
import { Item, CreateItemData, UpdateItemData } from '../../types/item';

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '9px 12px', background: '#f4efe2',
  border: '1px solid rgba(92,78,51,0.25)', borderRadius: 4,
  fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#5b5a52',
  outline: 'none', colorScheme: 'light',
};
const labelStyle: React.CSSProperties = {
  display: 'block', fontFamily: "'Inter', sans-serif", fontSize: 9,
  fontWeight: 500, letterSpacing: '0.2em', textTransform: 'uppercase',
  color: '#6b6a5c', marginBottom: 6,
};

interface ItemFormProps {
  item?: Item;
  onSubmit: (data: CreateItemData | UpdateItemData) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

export const ItemForm: React.FC<ItemFormProps> = ({ item, onSubmit, onCancel, isLoading = false }) => {
  const [formData, setFormData] = useState({
    name: '', description: '', sku: '', unit: 'pcs',
    sellingPrice: '', costPrice: '', taxRate: '', isActive: true,
  });
  const [errors, setErrors] = useState<Record<string, string[]>>({});

  useEffect(() => {
    if (item) {
      setFormData({
        name: item.name, description: item.description || '', sku: item.sku,
        unit: item.unit, sellingPrice: item.sellingPrice.toString(),
        costPrice: item.costPrice.toString(), taxRate: item.taxRate?.toString() || '',
        isActive: item.isActive,
      });
    }
  }, [item]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: [] }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string[]> = {};
    if (!formData.name.trim()) newErrors.name = ['Item name is required'];
    if (!formData.sku.trim()) newErrors.sku = ['SKU is required'];
    if (!formData.unit.trim()) newErrors.unit = ['Unit is required'];
    if (formData.sellingPrice && isNaN(Number(formData.sellingPrice))) newErrors.sellingPrice = ['Selling price must be a valid number'];
    if (formData.costPrice && isNaN(Number(formData.costPrice))) newErrors.costPrice = ['Cost price must be a valid number'];
    if (formData.taxRate && isNaN(Number(formData.taxRate))) newErrors.taxRate = ['Tax rate must be a valid number'];
    if (formData.taxRate && Number(formData.taxRate) < 0) newErrors.taxRate = ['Tax rate cannot be negative'];
    if (formData.taxRate && Number(formData.taxRate) > 100) newErrors.taxRate = ['Tax rate cannot be greater than 100%'];
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    onSubmit({
      name: formData.name.trim(), description: formData.description.trim() || undefined,
      sku: formData.sku.trim(), unit: formData.unit.trim(),
      sellingPrice: Number(formData.sellingPrice) || 0, costPrice: Number(formData.costPrice) || 0,
      taxRate: formData.taxRate ? Number(formData.taxRate) : undefined, isActive: formData.isActive,
    });
  };

  const errStyle = (field: string): React.CSSProperties => ({
    ...inputStyle, border: errors[field]?.length ? '1px solid rgba(201,90,90,0.4)' : '1px solid rgba(92,78,51,0.25)',
  });

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(7,7,15,0.85)', overflowY: 'auto', zIndex: 50, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: 80, paddingBottom: 40 }}>
      <div style={{ background: '#ffffff', border: '1px solid rgba(92,78,51,0.25)', borderRadius: 8, padding: '28px 32px', width: '100%', maxWidth: 640 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, paddingBottom: 16, borderBottom: '1px solid rgba(92,78,51,0.15)' }}>
          <h3 style={{ fontFamily: "'Inter', sans-serif", fontSize: 22, fontWeight: 300, color: '#1c2033', margin: 0 }}>
            {item ? 'Edit Item' : 'Create New Item'}
          </h3>
          <button onClick={onCancel} style={{ background: 'none', border: '1px solid rgba(92,78,51,0.25)', borderRadius: 4, color: '#6b6a5c', cursor: 'pointer', padding: '4px 8px', fontSize: 16, lineHeight: 1 }}>✕</button>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div>
              <label style={labelStyle}>Item Name *</label>
              <input type="text" name="name" value={formData.name} onChange={handleChange} placeholder="Enter item name" style={errStyle('name')} />
              {errors.name?.map((e, i) => <p key={i} style={{ margin: '4px 0 0', fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#c95a5a' }}>{e}</p>)}
            </div>
            <div>
              <label style={labelStyle}>SKU *</label>
              <input type="text" name="sku" value={formData.sku} onChange={handleChange} placeholder="Enter SKU" style={errStyle('sku')} />
              {errors.sku?.map((e, i) => <p key={i} style={{ margin: '4px 0 0', fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#c95a5a' }}>{e}</p>)}
            </div>
          </div>

          <div>
            <label style={labelStyle}>Description</label>
            <textarea name="description" value={formData.description} onChange={handleChange} rows={3} placeholder="Enter item description" style={{ ...inputStyle, resize: 'vertical', minHeight: 72 }} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
            <div>
              <label style={labelStyle}>Unit *</label>
              <input type="text" name="unit" value={formData.unit} onChange={handleChange} placeholder="e.g., pcs, kg" style={errStyle('unit')} />
              {errors.unit?.map((e, i) => <p key={i} style={{ margin: '4px 0 0', fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#c95a5a' }}>{e}</p>)}
            </div>
            <div>
              <label style={labelStyle}>Selling Price</label>
              <input type="number" name="sellingPrice" value={formData.sellingPrice} onChange={handleChange} min="0" step="0.01" placeholder="0.00" style={errStyle('sellingPrice')} />
              {errors.sellingPrice?.map((e, i) => <p key={i} style={{ margin: '4px 0 0', fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#c95a5a' }}>{e}</p>)}
            </div>
            <div>
              <label style={labelStyle}>Cost Price</label>
              <input type="number" name="costPrice" value={formData.costPrice} onChange={handleChange} min="0" step="0.01" placeholder="0.00" style={errStyle('costPrice')} />
              {errors.costPrice?.map((e, i) => <p key={i} style={{ margin: '4px 0 0', fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#c95a5a' }}>{e}</p>)}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div>
              <label style={labelStyle}>Tax Rate (%)</label>
              <input type="number" name="taxRate" value={formData.taxRate} onChange={handleChange} min="0" max="100" step="0.01" placeholder="0.00" style={errStyle('taxRate')} />
              {errors.taxRate?.map((e, i) => <p key={i} style={{ margin: '4px 0 0', fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#c95a5a' }}>{e}</p>)}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingTop: 22 }}>
              <input type="checkbox" name="isActive" checked={formData.isActive} onChange={handleChange} id="isActive" style={{ accentColor: '#d97706', width: 16, height: 16 }} />
              <label htmlFor="isActive" style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#5b5a52', cursor: 'pointer' }}>Active</label>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, paddingTop: 16, borderTop: '1px solid rgba(92,78,51,0.15)' }}>
            <button type="button" onClick={onCancel} style={{ padding: '10px 24px', background: 'none', border: '1px solid rgba(92,78,51,0.25)', borderRadius: 4, fontFamily: "'Inter', sans-serif", fontSize: 9, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#6b6a5c', cursor: 'pointer' }}>Cancel</button>
            <button type="submit" disabled={isLoading} style={{ padding: '10px 24px', background: isLoading ? 'rgba(92,78,51,0.25)' : 'linear-gradient(135deg, #d97706, #b45309)', border: 'none', borderRadius: 4, fontFamily: "'Inter', sans-serif", fontSize: 9, fontWeight: 500, letterSpacing: '0.15em', textTransform: 'uppercase', color: isLoading ? '#c9c2ab' : '#1c2033', cursor: isLoading ? 'not-allowed' : 'pointer' }}>
              {isLoading ? 'Saving...' : (item ? 'Update' : 'Create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
